import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Multiselect, DropdownList } from 'react-widgets';
import Loading from './Loading';
import Controller from '../controllers/DynamicSelectSegmentController';
import debounce from '../helpers/debounce';
import query from '../helpers/query';
import typeOf from '../helpers/typeOf';
import { I18nContext } from 'utils/I18n/context';

const minLength = 3;

function DynamicSelectSegment(props) {
    const { segment, values: valuesProp, count, multiselect, extraParams } = props;
    const { t } = useContext(I18nContext);
    const [values, setValues] = useState(valuesProp || []);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(null);
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [list, setList] = useState([]);
    const elemRef = useRef(null);
    const isSelectRef = useRef(false);

    const messagesStatic = t.multiselect;
    const reportPageStatic = t.reportPage;

    useEffect(() => {
        setValues(Array.isArray(valuesProp) ? valuesProp : []);
    }, [valuesProp]);

    useEffect(() => {
        setList([]);
        setPage(1);
        setTotal(null);
        setSearchTerm('');
        initValues();
    }, [segment]);

    const initValues = useCallback(() => {
        if (!Array.isArray(valuesProp) || valuesProp.length === 0) {
            setValues(multiselect ? [] : null);
            return;
        }
        
        if (valuesProp.every(val => typeof val === 'string' || typeof val === 'number')) {
            Controller.searchDataSegment({
                segment,
                params: {
                    ...extraParams,
                    ...query.parseToSend({ ids: valuesProp }),
                    count: valuesProp.length
                }
            }, (data) => {
                if (data && data.list && Array.isArray(data.list)) {
                    setValues(multiselect ? data.list : data.list[0]);
                } else {
                    console.warn('Invalid data in initValues:', data);
                    setValues(multiselect ? [] : null);
                }
            });
        } else {
            setValues(multiselect ? valuesProp : valuesProp[0]);
        }
    }, [segment, valuesProp, extraParams, multiselect]);

    const onSuccessList = (data) => {
        if (data && data.list && Array.isArray(data.list)) {
            setList(prev => prev.concat(data.list));
            setPage(data.page || 1);
            setTotal(data.total || 0);
        } else {
            console.warn('Invalid data in onSuccessList:', data);
        }
        setLoading(false);
    };

    const onSuccessId = (data) => {
        if (data && data.list && Array.isArray(data.list) && data.list.length > 0) {
            setList(prev => [data.list[0], ...prev]);
        } else {
            console.warn('Invalid data in onSuccessId:', data);
        }
    };

    const searchList = useCallback(() => {
        if (!segment) return;
        const term = searchTerm.trim();
        
        if (!term.length || term.length >= minLength || term.length && 'Country' === segment) {
            setLoading(true);
            Controller.searchDataSegment({
                segment,
                params: {
                    ...extraParams,
                    count,
                    page,
                    q: term,
                    types: segment.toLowerCase()
                }
            }, onSuccessList);
        }
        
        if (term.length && typeOf.isNumeric(term) && page === 1) {
            Controller.searchDataSegment({
                segment,
                params: {
                    ...extraParams,
                    ids: term
                }
            }, onSuccessId);
        }
    }, [segment, extraParams, count, page, searchTerm]);

    const debounceSearchList = useMemo(() => debounce(searchList, 300), [searchList]);

    const getList = () => {
        const result = Array.isArray(list) ? [...list] : [];
        if (page < total) {
            result.push({ value: 'more', title: reportPageStatic.more });
        }
        return result;
    };

    const onMore = () => {
        setPage(prev => prev + 1);
        searchList();
        if (elemRef.current) {
            const input = elemRef.current.querySelector('input.rw-input-reset');
            input && input.focus();
        }
    };

    const onSelect = () => {
        isSelectRef.current = true;
    };

    const onChange = (value) => {
        if (multiselect) {
            if (Array.isArray(value)) {
                const hasMore = value.some((v) => v && v.value === 'more');
                if (hasMore) {
                    const filtered = value.filter((v) => v && v.value !== 'more');
                    setValues(filtered);
                    props.onAction(filtered);
                    onMore();
                    setOpen(true);
                    return;
                }
                setValues(value);
                props.onAction(value);
                return;
            }
            setValues(value ? [value] : []);
            props.onAction(value ? [value] : []);
            return;
        }

        if (value && value.value === 'more') {
            onMore();
            setOpen(true);
            return;
        }
        setValues(value);
        props.onAction(value);
    };

    const onSearch = (term) => {
        if (isSelectRef.current || searchTerm === term) return false;
        
        if (term !== searchTerm) {
            setList([]);
            setPage(1);
            setTotal(null);
        }
        
        setSearchTerm(term);
        debounceSearchList();
        return false;
    };

    const onToggle = (isOpen) => {
        isSelectRef.current = false;
        
        if (!open && isOpen && !list.length) {
            searchList();
        }
        setOpen(isOpen);
    };

    const onFilter = () => true;

    const Component = multiselect ? Multiselect : DropdownList;

    return (
        <div className={`custom-multiselect ${loading ? 'custom-multiselect_loading' : ''}`} ref={elemRef}>
            <Component
                className={multiselect ? '' : 'rw-dropdownlist-select'}
                showPlaceholderWithValues
                placeholder={reportPageStatic.enterValue}
                minLength={minLength}
                dataKey={'value'}
                textField={'title'}
                data={getList()}
                open={open}
                value={Array.isArray(values) ? values : (multiselect ? [] : values)}
                filter={onFilter}
                onChange={onChange}
                onSelect={onSelect}
                onToggle={onToggle}
                onSearch={onSearch}
                searchTerm={searchTerm}
                messages={messagesStatic}
            />
            {loading && !list.length ? <div className="custom-multiselect__loading"><Loading inherit={true} /></div> : null}
        </div>
    );
}

DynamicSelectSegment.propTypes = {
    count: PropTypes.number,
    values: PropTypes.array,
    multiselect: PropTypes.bool,
    extraParams: PropTypes.object
};

DynamicSelectSegment.defaultProps = {
    count: 15,
    values: [],
    multiselect: false,
    extraParams: {}
};

export default DynamicSelectSegment;